/**
 * The groups a master nursery sublicence is written against, and who holds one.
 *
 * The thing this screen has to make obvious is what a group does and does not
 * contain, because it is not what the word suggests.
 *
 *   A group holds NURSERIES. They arrive by signing a master agreement, which
 *   the group is the scope of.
 *   A group holds NO CULTIVARS. Which cultivars a nursery may grow is ticked
 *   nursery by nursery on each cultivar's own edit screen.
 *
 * So there is no "add cultivars to this group" control here, and its absence is
 * deliberate rather than unfinished. A group that could absorb cultivars would
 * let one click hand every nursery in a tier rights to something, and the first
 * anyone would know of it is a catalogue listing. Each card says where the
 * decision actually lives instead of offering a shortcut that shouldn't exist.
 *
 * Each group names the contract its nurseries sign as their master, and the
 * one issued under it for each website order. Issuing a master offers no
 * choice of document: the group decides, and the version sent is always the
 * one in force.
 *
 * Closing a group is likewise not a delete. Masters already signed against it
 * keep running on their own terms — a licensor closing a tier is deciding not to
 * admit anyone new, not tearing up agreements.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Handshake, Loader2, Plus, AlertCircle, Building2, ChevronDown, ChevronRight,
  Home, X, FileSignature,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import cultivarService, {
  type SublicenceGroup,
  type GroupNursery,
} from '../../services/cultivar';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import contractService from '../../services/contracts';
import type { ContractFamily } from '../../types/contracts';
import { ContractHistory } from '../../components/cultivar/contracts/ContractBits';

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  pending_confirmation: 'bg-amber-50 text-amber-900 border-amber-200',
  suspended: 'bg-red-50 text-red-800 border-red-200',
  terminated: 'bg-neutral-100 text-neutral-500 border-neutral-200',
  expired: 'bg-neutral-100 text-neutral-500 border-neutral-200',
  rejected: 'bg-neutral-100 text-neutral-500 border-neutral-200',
  superseded: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Live',
  pending_confirmation: 'Awaiting signature',
  suspended: 'Suspended',
  terminated: 'Terminated',
  expired: 'Expired',
  rejected: 'Rejected',
  superseded: 'Withdrawn',
};

// A group slot holds a contract of one kind only; the server refuses others.
const MASTER_TIER = 'nursery_master_sublicence';
const SCHEDULE_TIER = 'nursery_propagation';

export default function SublicenceGroups() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const { hasAccess, loading: licenceLoading } = useModuleLicense('cultivar', ['pbr', 'admin']);
  const { hasAccess: isAdmin } = useModuleLicense('cultivar', 'admin');

  const [groups, setGroups] = useState<SublicenceGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expanded, setExpanded] = useState<number | null>(null);
  const [nurseries, setNurseries] = useState<Record<number, GroupNursery[]>>({});
  const [nurseriesLoading, setNurseriesLoading] = useState(false);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', description: '', contract_family_id: 0, schedule_contract_family_id: 0 });
  const [saving, setSaving] = useState(false);

  const [families, setFamilies] = useState<ContractFamily[]>([]);
  const [contractEdit, setContractEdit] = useState<Record<number, { master: number; schedule: number }>>({});
  const [historyKey, setHistoryKey] = useState(0);
  const masterFamilies = families.filter((f) => f.license_tier === MASTER_TIER);
  const scheduleFamilies = families.filter((f) => f.license_tier === SCHEDULE_TIER && !f.cultivar_id);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await cultivarService.listSublicenceGroups(true);
      if (res.success && res.data) setGroups(res.data);
      else setError(res.message || 'Could not load groups.');
    } catch {
      setError('Could not load groups.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (!licenceLoading && hasAccess) load(); }, [licenceLoading, hasAccess, load]);

  useEffect(() => {
    if (licenceLoading || !hasAccess) return;
    contractService.list()
      .then((res) => { if (res.success) setFamilies(res.data ?? []); })
      .catch(() => { /* the pickers say there is nothing to choose */ });
  }, [licenceLoading, hasAccess]);

  const saveContracts = async (g: SublicenceGroup) => {
    const edit = contractEdit[g.id];
    if (!edit) return;
    setError(null);
    try {
      const res = await cultivarService.updateSublicenceGroup(g.id, {
        contract_family_id: edit.master || null,
        schedule_contract_family_id: edit.schedule || null,
      });
      if (res.success) {
        setContractEdit((prev) => { const n = { ...prev }; delete n[g.id]; return n; });
        setHistoryKey((k) => k + 1);
        await load();
      } else {
        setError(res.error.message);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the group\u2019s contracts.');
    }
  };

  const toggle = async (id: number) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (nurseries[id]) return;
    setNurseriesLoading(true);
    try {
      const res = await cultivarService.listGroupNurseries(id);
      if (res.success && res.data) setNurseries((prev) => ({ ...prev, [id]: res.data! }));
    } catch {
      // An expander that fails to fill is not worth an error banner over the
      // whole page; the empty state below reads correctly either way.
    } finally {
      setNurseriesLoading(false);
    }
  };

  const create = async () => {
    setError(null);
    if (!form.code.trim()) { setError('A group code is required.'); return; }
    setSaving(true);
    try {
      const res = await cultivarService.createSublicenceGroup({
        code: form.code.trim(),
        name: form.name.trim() || form.code.trim(),
        description: form.description.trim() || undefined,
        contract_family_id: form.contract_family_id || null,
        schedule_contract_family_id: form.schedule_contract_family_id || null,
      });
      if (res.success) {
        setCreating(false);
        setForm({ code: '', name: '', description: '', contract_family_id: 0, schedule_contract_family_id: 0 });
        await load();
      } else {
        setError(res.message || 'Could not create the group.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the group.');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (g: SublicenceGroup, status: 'active' | 'closed') => {
    setError(null);
    try {
      const res = await cultivarService.updateSublicenceGroup(g.id, { status });
      if (res.success) { setHistoryKey((k) => k + 1); await load(); }
      else setError(res.error.message);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update the group.');
    }
  };

  if (licenceLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Sublicence Groups" icon={<Handshake className="h-6 w-6 text-white" />} />
        <div className="flex justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Sublicence Groups" icon={<Handshake className="h-6 w-6 text-white" />} />
        <div className="max-w-3xl mx-auto px-4 py-12">
          <Card><CardContent className="p-8 text-center text-sm text-neutral-600">
            A Cultivar PBR or Admin licence seat is required to manage sublicence groups.
          </CardContent></Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Sublicence Groups"
        subtitle="The categories your master nursery agreements are written against"
        icon={<Handshake className="h-6 w-6 text-white" />}
        actionButtons={
          isAdmin ? (
            <Button variant="outline" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New group
            </Button>
          ) : undefined
        }
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {error && (
          <div className="flex items-start gap-2 rounded-md bg-error-50 border border-error-200 p-3 text-sm text-error-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span className="min-w-0 break-words">{error}</span>
          </div>
        )}

        <Card>
          <CardContent className="p-5 text-sm text-neutral-600">
            <p className="mb-2">
              A nursery signs <strong>one master agreement</strong> against a group. That admits
              it to the group and to nothing else.
            </p>
            <p>
              A group holds <strong>nurseries</strong>, not cultivars. Which cultivars a nursery
              may grow is ticked nursery by nursery on each cultivar's own screen — so a nursery
              never gains rights to a cultivar nobody consciously granted it.
            </p>
          </CardContent>
        </Card>

        {creating && (
          <Card>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-neutral-900">New sublicence group</h3>
                <button type="button" onClick={() => setCreating(false)} className="text-neutral-400 hover:text-neutral-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-neutral-700 mb-1">Code *</label>
                  <input
                    value={form.code}
                    maxLength={32}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="A"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <p className="mt-1 text-xs text-neutral-500">Your own shorthand — "A", "PREMIUM".</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-neutral-700 mb-1">Name</label>
                  <input
                    value={form.name}
                    maxLength={255}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Group A"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  rows={2}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What this tier covers commercially."
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <ContractPickers
                idPrefix="new"
                master={form.contract_family_id}
                schedule={form.schedule_contract_family_id}
                masterFamilies={masterFamilies}
                scheduleFamilies={scheduleFamilies}
                onChange={(master, schedule) => setForm({ ...form, contract_family_id: master, schedule_contract_family_id: schedule })}
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCreating(false)} disabled={saving}>Cancel</Button>
                <Button onClick={create} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Create group
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {groups.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-neutral-600">
              No sublicence groups yet. A group is the category a nursery's master agreement is
              written against — create one before issuing a master.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => {
              const open = expanded === g.id;
              const rows = nurseries[g.id] || [];
              return (
                <Card key={g.id}>
                  <CardContent className="p-0">
                    <button
                      type="button"
                      onClick={() => toggle(g.id)}
                      className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-neutral-50"
                    >
                      {open
                        ? <ChevronDown className="h-4 w-4 mt-1 flex-shrink-0 text-neutral-400" />
                        : <ChevronRight className="h-4 w-4 mt-1 flex-shrink-0 text-neutral-400" />}
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-neutral-900 break-words">
                            {g.code} — {g.name}
                          </span>
                          {g.status === 'closed' && (
                            <span className="text-xs rounded-full border border-neutral-200 bg-neutral-100 text-neutral-600 px-2 py-0.5">
                              Closed to new masters
                            </span>
                          )}
                        </span>
                        <span className="block text-xs mt-0.5">
                          {g.contract_family_id ? (
                            <span className="text-neutral-600 inline-flex items-center gap-1">
                              <FileSignature className="h-3 w-3 flex-shrink-0" />
                              {g.contract_family_name}
                              {g.contract_current_version ? ` v${g.contract_current_version}` : ' (not published)'}
                            </span>
                          ) : (
                            <span className="text-amber-700">No master contract set — masters cannot be issued</span>
                          )}
                        </span>
                        <span className="block text-xs text-neutral-500 mt-0.5">
                          {g.active_nursery_count ?? 0} nurser
                          {(g.active_nursery_count ?? 0) === 1 ? 'y' : 'ies'} with a live master
                        </span>
                        {g.description && (
                          <span className="block text-xs text-neutral-500 mt-1 break-words">{g.description}</span>
                        )}
                      </span>
                    </button>

                    {open && (
                      <div className="border-t border-neutral-100 px-5 py-4 space-y-3">
                        {nurseriesLoading && rows.length === 0 ? (
                          <div className="flex items-center gap-2 text-sm text-neutral-500">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading nurseries…
                          </div>
                        ) : rows.length === 0 ? (
                          <p className="text-sm text-neutral-600">
                            No nursery has been issued a master against this group yet.
                          </p>
                        ) : (
                          <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg overflow-hidden">
                            {rows.map((n) => (
                              <li key={n.license_id} className="px-4 py-3 flex items-start gap-3">
                                {n.is_house_nursery
                                  ? <Home className="h-4 w-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                                  : <Building2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-indigo-600" />}
                                <span className="min-w-0 flex-1">
                                  <span className="block text-sm font-medium text-neutral-900 break-words">
                                    {n.nursery_name}
                                  </span>
                                  <span className="block text-xs text-neutral-500">
                                    Master #{n.license_id}
                                    {n.end_date
                                      ? ` · expires ${formatLocalDateInTz(n.end_date, orgTz)}`
                                      : ' · no end date'}
                                    {n.region ? ` · ${n.region}` : ''}
                                  </span>
                                </span>
                                <span
                                  className={`text-xs rounded-full border px-2 py-0.5 flex-shrink-0 ${
                                    STATUS_STYLES[n.status] || 'bg-neutral-100 text-neutral-600 border-neutral-200'
                                  }`}
                                >
                                  {STATUS_LABELS[n.status] || n.status}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}

                        <p className="text-xs text-neutral-500">
                          Which cultivars these nurseries may grow is ticked on each cultivar's
                          own edit screen.
                        </p>

                        {isAdmin && (
                          <div className="border border-neutral-200 rounded-lg p-3 space-y-2">
                            <ContractPickers
                              idPrefix={`g${g.id}`}
                              master={contractEdit[g.id]?.master ?? g.contract_family_id ?? 0}
                              schedule={contractEdit[g.id]?.schedule ?? g.schedule_contract_family_id ?? 0}
                              masterFamilies={masterFamilies}
                              scheduleFamilies={scheduleFamilies}
                              onChange={(master, schedule) => setContractEdit((prev) => ({ ...prev, [g.id]: { master, schedule } }))}
                            />
                            {contractEdit[g.id] && (
                              <div className="flex flex-wrap justify-end gap-2">
                                <Button variant="outline" size="sm" onClick={() => setContractEdit((prev) => { const n = { ...prev }; delete n[g.id]; return n; })}>
                                  Cancel
                                </Button>
                                <Button size="sm" onClick={() => saveContracts(g)}>Save contracts</Button>
                              </div>
                            )}
                          </div>
                        )}

                        <ContractHistory kind="group" id={g.id} title="Changes to this group" refreshKey={historyKey} />

                        {isAdmin && (
                          <div className="flex flex-wrap justify-end gap-2 pt-1">
                            {g.status === 'active' ? (
                              <Button variant="outline" onClick={() => setStatus(g, 'closed')}>
                                Close to new masters
                              </Button>
                            ) : (
                              <Button variant="outline" onClick={() => setStatus(g, 'active')}>
                                Reopen
                              </Button>
                            )}
                            <Button variant="outline" onClick={() => navigate('/cultivar')}>
                              Issue a master
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The two contracts a group names. An unpublished contract is listed (and says
 * so) so a group can be set up before its contract is finished; nothing can be
 * sent on it until it is published.
 */
function ContractPickers({ idPrefix, master, schedule, masterFamilies, scheduleFamilies, onChange }: {
  idPrefix: string;
  master: number;
  schedule: number;
  masterFamilies: ContractFamily[];
  scheduleFamilies: ContractFamily[];
  onChange: (master: number, schedule: number) => void;
}) {
  const label = (f: ContractFamily) => `${f.name}${f.current_version ? ` (v${f.current_version})` : ' (not published)'}`;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="min-w-0">
        <label htmlFor={`${idPrefix}-master`} className="block text-xs font-semibold text-neutral-700 mb-1">Master contract</label>
        <select
          id={`${idPrefix}-master`}
          value={master}
          onChange={(e) => onChange(Number(e.target.value), schedule)}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value={0}>— none —</option>
          {masterFamilies.map((f) => <option key={f.id} value={f.id}>{label(f)}</option>)}
        </select>
        <p className="mt-1 text-xs text-neutral-500">What every nursery in this group signs. Masters cannot be issued without one.</p>
      </div>
      <div className="min-w-0">
        <label htmlFor={`${idPrefix}-schedule`} className="block text-xs font-semibold text-neutral-700 mb-1">Per-order schedule contract</label>
        <select
          id={`${idPrefix}-schedule`}
          value={schedule}
          onChange={(e) => onChange(master, Number(e.target.value))}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value={0}>— none —</option>
          {scheduleFamilies.map((f) => <option key={f.id} value={f.id}>{label(f)}</option>)}
        </select>
        <p className="mt-1 text-xs text-neutral-500">Issued under the master for each website order.</p>
      </div>
    </div>
  );
}
